import { AlertTriangle, ArrowRight, BookOpenText, Check, CircleDashed, Merge, Plus, Undo2, X } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useApp } from "@/lib/AppContext";
import {
  ApiError, type EntitySuggestion, type EntityType, type MergeSuggestion, type OpenLoop, type OpenLoopAnswer,
} from "@/lib/api";
import { ENTITY_TYPES } from "@/lib/entityTypes";
import { formatDate } from "@/lib/format";
import { subjectKindMeta } from "@/lib/subjectKinds";

/**
 * UI-15 — the Assistant hub, complete (AUTO-4, M10.3; AUTO-2, M10.4).
 *
 * The handoff's four are Recaps, Open loops, Tomorrow and Suggestions. Open loops and
 * Suggestions have something behind them; Recaps and Tomorrow arrive with AUTO-3/AUTO-5
 * (M10.2), where UI-15 is listed as finished. Until then those two say so rather than showing
 * an empty list, which is the same honesty M1.2 gave every unfurnished room — an empty list
 * means "nothing yet", and that is a different sentence from "not built yet".
 *
 * <b>Two columns, and the split is the feature.</b> "You owe" and "Owed to you" come from
 * SPCH-7's "is me" link; without it the detection pass produces nothing at all, and the empty
 * state says which link is missing rather than leaving somebody to wonder why a busy month
 * produced no loops.
 *
 * <b>Suggestions never merges on its own.</b> A row offers both names as separate buttons —
 * "Keep '<i>A</i>'" and "Keep '<i>B</i>'" — because which spelling should survive, if either
 * should, is the owner's call and not something a similarity score gets to make quietly.
 *
 * <b>Suggestions also holds suggested entities (WORLD-9, M17.1)</b>: pages synthesis opened for
 * somebody or something the owner never created. Synthesis still never mints an entity — the
 * card does, when the owner picks the kind and the name and presses create; a person's role is
 * asked for on the same card. "Not worth an entity" is final.
 */

interface Tab {
  id: string;
  label: string;
  /** The increment that fills this tab, or undefined once one has. */
  milestone?: string;
}

const TABS: Tab[] = [
  { id: "open-loops", label: "Open loops" },
  { id: "recaps", label: "Recaps", milestone: "M10.2" },
  { id: "tomorrow", label: "Tomorrow", milestone: "M10.2" },
  { id: "suggestions", label: "Suggestions" },
];

export function AssistantPage() {
  const { api, refreshEntities, refreshPeople } = useApp();
  const [params, setParams] = useSearchParams();
  const [loops, setLoops] = useState<OpenLoop[] | null>(null);
  const [suggestions, setSuggestions] = useState<MergeSuggestion[] | null>(null);
  const [pages, setPages] = useState<EntitySuggestion[] | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [working, setWorking] = useState<string | null>(null);

  const tab = params.get("tab") ?? "open-loops";

  const load = useCallback(async () => {
    try {
      setLoops(await api.getOpenLoops());
      setProblem(null);
    } catch (failure) {
      setProblem(failure instanceof ApiError ? failure.message : "Could not read your open loops.");
    }
  }, [api]);

  const loadSuggestions = useCallback(async () => {
    // Two queues read side by side, each standing on its own: a page suggestion that cannot be
    // read must not hide the merges, and the other way round.
    void (async () => {
      try {
        setPages(await api.getEntitySuggestions());
      } catch {
        setPages([]);
      }
    })();

    try {
      setSuggestions(await api.getMergeSuggestions());
      setProblem(null);
    } catch (failure) {
      setProblem(failure instanceof ApiError ? failure.message : "Could not read the merge suggestions.");
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (tab === "suggestions" && suggestions === null) void loadSuggestions();
  }, [tab, suggestions, loadSuggestions]);

  async function answer(id: string, action: OpenLoopAnswer) {
    setWorking(id);
    try {
      setLoops(await api.answerOpenLoop(id, action));
      setProblem(null);
    } catch (failure) {
      setProblem(failure instanceof ApiError ? failure.message : "That answer did not go through.");
    } finally {
      setWorking(null);
    }
  }

  async function accept(id: string, keepEntityId: string) {
    setWorking(id);
    try {
      setSuggestions(await api.acceptMergeSuggestion(id, keepEntityId));
      setProblem(null);
    } catch (failure) {
      setProblem(failure instanceof ApiError ? failure.message : "That merge did not go through.");
    } finally {
      setWorking(null);
    }
  }

  async function acceptPage(id: string, input: { type: EntityType; name: string; role: string }) {
    setWorking(id);
    try {
      const answer = await api.acceptEntitySuggestion(id, input);
      setPages(answer.suggestions);
      refreshEntities();
      refreshPeople();
      setProblem(null);
      return answer.entityId;
    } catch (failure) {
      setProblem(failure instanceof ApiError ? failure.message : "That entity could not be created.");
      return null;
    } finally {
      setWorking(null);
    }
  }

  async function rejectPage(id: string) {
    setWorking(id);
    try {
      setPages(await api.rejectEntitySuggestion(id));
      setProblem(null);
    } catch (failure) {
      setProblem(failure instanceof ApiError ? failure.message : "That answer did not go through.");
    } finally {
      setWorking(null);
    }
  }

  async function reject(id: string) {
    setWorking(id);
    try {
      setSuggestions(await api.rejectMergeSuggestion(id));
      setProblem(null);
    } catch (failure) {
      setProblem(failure instanceof ApiError ? failure.message : "That answer did not go through.");
    } finally {
      setWorking(null);
    }
  }

  const mine = useMemo(() => (loops ?? []).filter((loop) => loop.direction === "owed_by_me"), [loops]);
  const theirs = useMemo(() => (loops ?? []).filter((loop) => loop.direction === "owed_to_me"), [loops]);

  return (
    <div className="flex flex-col gap-[var(--space-6)] px-[var(--space-8)] py-[var(--space-8)]">
      <header className="flex flex-col gap-[var(--space-2)]">
        <h1 className="text-[length:var(--text-2xl)] font-semibold text-[var(--text-primary)]">Assistant</h1>
        <p className="text-[length:var(--text-sm)] text-[var(--text-muted)]">
          What the system worked out while you were away. Everything here is built on approved
          insights only.
        </p>
      </header>

      <nav className="flex items-center gap-[var(--space-1)] border-b border-[var(--border-subtle)]" role="tablist">
        {TABS.map((entry) => {
          const active = entry.id === tab;
          const count = entry.id === "open-loops" ? loops?.length
            : entry.id === "suggestions" ? (suggestions === null ? undefined : suggestions.length + (pages?.length ?? 0))
            : undefined;

          return (
            <button
              key={entry.id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => setParams(entry.id === "open-loops" ? {} : { tab: entry.id })}
              className={
                active
                  ? "-mb-px border-b-2 border-[var(--primary)] px-[var(--space-4)] py-[var(--space-2)] text-[length:var(--text-sm)] font-semibold text-[var(--text-primary)]"
                  : "-mb-px border-b-2 border-transparent px-[var(--space-4)] py-[var(--space-2)] text-[length:var(--text-sm)] font-medium text-[var(--text-muted)] hover:text-[var(--text-primary)]"
              }
            >
              {entry.label}
              {count !== undefined && count > 0 && (
                <span className="ml-[var(--space-2)] font-mono text-[length:var(--text-xs)] tabular-nums text-[var(--text-muted)]">
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </nav>

      {problem && (
        <p
          role="alert"
          className="flex items-start gap-[var(--space-2)] rounded-[var(--radius-md)] border border-[var(--destructive)]/40 px-[var(--space-3)] py-[var(--space-2)] text-[length:var(--text-xs)] text-[var(--destructive)]"
        >
          <AlertTriangle className="mt-px size-3.5 shrink-0" aria-hidden />
          {problem}
        </p>
      )}

      {tab === "open-loops" ? (
        <OpenLoops
          mine={mine}
          theirs={theirs}
          loading={loops === null}
          working={working}
          onAnswer={answer}
        />
      ) : tab === "suggestions" ? (
        <div className="flex flex-col gap-[var(--space-8)]">
          <SuggestedEntities pages={pages} working={working} onAccept={acceptPage} onReject={rejectPage} />
          <Suggestions
            suggestions={suggestions}
            working={working}
            onAccept={accept}
            onReject={reject}
          />
        </div>
      ) : (
        <NotYet milestone={TABS.find((entry) => entry.id === tab)?.milestone ?? "a later milestone"} />
      )}
    </div>
  );
}

function OpenLoops({
  mine,
  theirs,
  loading,
  working,
  onAnswer,
}: {
  mine: OpenLoop[];
  theirs: OpenLoop[];
  loading: boolean;
  working: string | null;
  onAnswer: (id: string, action: OpenLoopAnswer) => void;
}) {
  if (loading) {
    return <p className="text-[length:var(--text-sm)] text-[var(--text-muted)]">Reading your open loops…</p>;
  }

  if (mine.length === 0 && theirs.length === 0) {
    return (
      <div className="flex flex-col gap-[var(--space-2)] rounded-[var(--radius-lg)] border border-dashed border-[var(--border-default)] px-[var(--space-6)] py-[var(--space-8)] text-center">
        <p className="text-[length:var(--text-sm)] text-[var(--text-primary)]">No open loops.</p>
        <p className="text-[length:var(--text-xs)] text-[var(--text-muted)]">
          Commitments are read from approved insights. If a meeting you have reviewed made
          promises and none appear here, check that a person in{" "}
          <Link to="/people" className="text-[var(--primary)] underline">
            People
          </Link>{" "}
          is linked to your account — without that, there is no way to tell who owes what.
        </p>
      </div>
    );
  }

  return (
    <div className="grid gap-[var(--space-6)] lg:grid-cols-2">
      <Column title="You owe" loops={mine} working={working} onAnswer={onAnswer} />
      <Column title="Owed to you" loops={theirs} working={working} onAnswer={onAnswer} />
    </div>
  );
}

function Column({
  title,
  loops,
  working,
  onAnswer,
}: {
  title: string;
  loops: OpenLoop[];
  working: string | null;
  onAnswer: (id: string, action: OpenLoopAnswer) => void;
}) {
  return (
    <section className="flex flex-col gap-[var(--space-3)]">
      <h2 className="text-[length:var(--text-xs)] font-semibold uppercase tracking-[var(--tracking-wide)] text-[var(--text-muted)]">
        {title}
        <span className="ml-[var(--space-2)] font-mono tabular-nums">{loops.length}</span>
      </h2>

      {loops.length === 0 ? (
        <p className="text-[length:var(--text-sm)] text-[var(--text-muted)]">Nothing here.</p>
      ) : (
        <ul className="flex flex-col gap-[var(--space-2)]">
          {loops.map((loop) => (
            <Loop key={loop.id} loop={loop} busy={working === loop.id} onAnswer={onAnswer} />
          ))}
        </ul>
      )}
    </section>
  );
}

function Loop({
  loop,
  busy,
  onAnswer,
}: {
  loop: OpenLoop;
  busy: boolean;
  onAnswer: (id: string, action: OpenLoopAnswer) => void;
}) {
  return (
    <li className="flex flex-col gap-[var(--space-2)] rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-[var(--surface-1)] px-[var(--space-4)] py-[var(--space-3)]">
      <div className="flex items-start gap-[var(--space-2)]">
        <CircleDashed className="mt-0.5 size-4 shrink-0 text-[var(--text-muted)]" aria-hidden />
        <p className="text-[length:var(--text-sm)] text-[var(--text-primary)]">{loop.statement}</p>
      </div>

      <div className="flex flex-wrap items-center gap-x-[var(--space-3)] gap-y-[var(--space-1)] pl-[calc(var(--space-4)+var(--space-2))] text-[length:var(--text-xs)]">
        {loop.counterparty && (
          <span className="text-[var(--text-muted)]">{loop.counterparty}</span>
        )}
        {loop.dueOn && (
          <span className={loop.overdue ? "font-semibold text-[var(--destructive)]" : "text-[var(--text-muted)]"}>
            {loop.overdue ? "Overdue — " : "Due "}
            {formatDate(loop.dueOn)}
          </span>
        )}
        <Link
          to={`/recordings/${loop.sourceMaterialId}?insight=${loop.insightId}`}
          className="inline-flex items-center gap-1 text-[var(--text-muted)] hover:text-[var(--primary)]"
        >
          {loop.sourceTitle ?? "Source"}
          <ArrowRight className="size-3" aria-hidden />
        </Link>
      </div>

      {/* A suggestion, never a closure. The pass noticed a later insight that looks like this
          being done; confirming it is the owner's, which is why the button says so. */}
      {loop.fulfilledBy && (
        <p className="pl-[calc(var(--space-4)+var(--space-2))] text-[length:var(--text-xs)] text-[var(--text-muted)]">
          A later insight suggests this is done.
        </p>
      )}

      <div className="flex items-center gap-[var(--space-2)] pl-[calc(var(--space-4)+var(--space-2))]">
        <button
          type="button"
          disabled={busy}
          onClick={() => onAnswer(loop.id, "done")}
          className="inline-flex items-center gap-1 rounded-[var(--radius-md)] border border-[var(--border-default)] px-[var(--space-3)] py-[var(--space-1)] text-[length:var(--text-xs)] font-medium text-[var(--text-primary)] hover:border-[var(--primary)] disabled:opacity-60"
        >
          <Check className="size-3" aria-hidden />
          Done
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => onAnswer(loop.id, "dropped")}
          className="inline-flex items-center gap-1 rounded-[var(--radius-md)] px-[var(--space-3)] py-[var(--space-1)] text-[length:var(--text-xs)] font-medium text-[var(--text-muted)] hover:text-[var(--text-primary)] disabled:opacity-60"
        >
          <X className="size-3" aria-hidden />
          Not happening
        </button>
      </div>
    </li>
  );
}

/**
 * WORLD-9's queue: pages the world view holds for things the owner never created. Rendered
 * only when there is one, above the merges — a page with nothing behind it is the more
 * consequential gap, because nothing the owner knows about it has anywhere to go.
 */
function SuggestedEntities({
  pages,
  working,
  onAccept,
  onReject,
}: {
  pages: EntitySuggestion[] | null;
  working: string | null;
  onAccept: (id: string, input: { type: EntityType; name: string; role: string }) => Promise<string | null>;
  onReject: (id: string) => void;
}) {
  if (!pages || pages.length === 0) return null;

  return (
    <section className="flex flex-col gap-[var(--space-3)]">
      <div className="flex flex-col gap-1">
        <h2 className="text-[length:var(--text-sm)] font-semibold text-[var(--text-primary)]">Pages with nothing behind them</h2>
        <p className="text-[length:var(--text-xs)] text-[var(--text-muted)]">
          Synthesis opened these pages for names you have not created. Create the entity and the page becomes its
          page — with an About section, relations and a place in the org chart.
        </p>
      </div>
      <ul className="flex flex-col gap-[var(--space-2)]">
        {pages.map((page) => (
          <SuggestedEntity
            key={page.id}
            page={page}
            busy={working === page.id}
            onAccept={onAccept}
            onReject={onReject}
          />
        ))}
      </ul>
    </section>
  );
}

const CREATABLE = ENTITY_TYPES.filter((meta) => meta.type !== "term");

function SuggestedEntity({
  page,
  busy,
  onAccept,
  onReject,
}: {
  page: EntitySuggestion;
  busy: boolean;
  onAccept: (id: string, input: { type: EntityType; name: string; role: string }) => Promise<string | null>;
  onReject: (id: string) => void;
}) {
  const navigate = useNavigate();
  const suggestedType = CREATABLE.some((meta) => meta.type === page.subjectKind) ? (page.subjectKind as EntityType) : "";
  const [type, setType] = useState<EntityType | "">(suggestedType);
  const [name, setName] = useState(page.title);
  const [role, setRole] = useState("");
  const Icon = subjectKindMeta(page.subjectKind).icon;
  const noun = type ? (CREATABLE.find((meta) => meta.type === type)?.label.toLowerCase() ?? "entity") : "entity";

  return (
    <li className="flex flex-col gap-[var(--space-3)] rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-[var(--surface-1)] px-[var(--space-4)] py-[var(--space-3)]">
      <div className="flex items-start gap-[var(--space-2)]">
        <Icon className="mt-0.5 size-4 shrink-0 text-[var(--text-muted)]" aria-hidden />
        <div className="min-w-0 flex-1">
          <p className="text-[length:var(--text-sm)] text-[var(--text-primary)]">
            Who or what is &ldquo;{page.title}&rdquo;? The world view holds {page.beliefCount}{" "}
            {page.beliefCount === 1 ? "belief" : "beliefs"} about it, and nothing you created stands behind it.
          </p>
          {page.summary && (
            <p className="mt-1 text-[length:var(--text-xs)] text-[var(--text-muted)]">{page.summary}</p>
          )}
        </div>
        <Link
          to={`/world-view/${page.slug}`}
          className="inline-flex shrink-0 items-center gap-1 text-[length:var(--text-xs)] text-[var(--text-muted)] hover:text-[var(--primary)]"
        >
          <BookOpenText className="size-3" aria-hidden />
          Page
        </Link>
      </div>

      <div className="flex flex-wrap items-center gap-[var(--space-2)] pl-[calc(var(--space-4)+var(--space-2))]">
        <select
          value={type}
          aria-label={`Kind of entity for ${page.title}`}
          onChange={(event) => setType(event.target.value as EntityType)}
          className="h-8 rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-2)] px-2 text-[length:var(--text-xs)] text-[var(--text-primary)]"
        >
          <option value="">Which kind?</option>
          {CREATABLE.map((meta) => (
            <option key={meta.type} value={meta.type}>
              {meta.label}
            </option>
          ))}
        </select>
        <input
          value={name}
          aria-label={`Name for ${page.title}`}
          onChange={(event) => setName(event.target.value)}
          className="h-8 w-[200px] rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-2)] px-2.5 text-[length:var(--text-xs)] text-[var(--text-primary)] outline-none focus:border-[var(--ring)]"
        />
        {type === "person" && (
          <input
            value={role}
            aria-label={`Role for ${page.title}`}
            placeholder="Role — e.g. Finance, or the customer they work for"
            onChange={(event) => setRole(event.target.value)}
            className="h-8 w-[260px] rounded-[var(--radius-md)] border border-[var(--border-default)] bg-[var(--surface-2)] px-2.5 text-[length:var(--text-xs)] text-[var(--text-primary)] outline-none focus:border-[var(--ring)]"
          />
        )}
        <button
          type="button"
          disabled={busy || !type || name.trim().length === 0}
          onClick={async () => {
            if (!type) return;
            const created = await onAccept(page.id, { type, name: name.trim(), role: role.trim() });
            if (created && type === "person") navigate(`/people/${created}`);
          }}
          className="inline-flex items-center gap-1 rounded-[var(--radius-md)] border border-[var(--border-default)] px-[var(--space-3)] py-[var(--space-1)] text-[length:var(--text-xs)] font-medium text-[var(--text-primary)] hover:border-[var(--primary)] disabled:opacity-60"
        >
          <Plus className="size-3" aria-hidden />
          Create {noun}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => onReject(page.id)}
          className="inline-flex items-center gap-1 rounded-[var(--radius-md)] px-[var(--space-3)] py-[var(--space-1)] text-[length:var(--text-xs)] font-medium text-[var(--text-muted)] hover:text-[var(--text-primary)] disabled:opacity-60"
        >
          <X className="size-3" aria-hidden />
          Not worth an entity
        </button>
      </div>
    </li>
  );
}

/**
 * AUTO-2's queue: pairs a periodic fuzzy pass thinks might be the same entity (M10.4).
 *
 * Each row is a plain choice between two names, never an "accept" that silently keeps one —
 * the pass found a spelling match, not which spelling is correct, and only the owner knows
 * that. Reject is a third answer, not a lesser one: "not the same thing" closes the row exactly
 * as finally as a merge does.
 */
function Suggestions({
  suggestions,
  working,
  onAccept,
  onReject,
}: {
  suggestions: MergeSuggestion[] | null;
  working: string | null;
  onAccept: (id: string, keepEntityId: string) => void;
  onReject: (id: string) => void;
}) {
  if (suggestions === null) {
    return <p className="text-[length:var(--text-sm)] text-[var(--text-muted)]">Reading merge suggestions…</p>;
  }

  if (suggestions.length === 0) {
    return (
      <div className="flex flex-col gap-[var(--space-2)] rounded-[var(--radius-lg)] border border-dashed border-[var(--border-default)] px-[var(--space-6)] py-[var(--space-8)] text-center">
        <p className="text-[length:var(--text-sm)] text-[var(--text-primary)]">No merge suggestions.</p>
        <p className="text-[length:var(--text-xs)] text-[var(--text-muted)]">
          A periodic pass compares entity names and aliases and lands candidates here. Nothing
          ever merges without you choosing which name survives.
        </p>
      </div>
    );
  }

  return (
    <ul className="flex flex-col gap-[var(--space-2)]">
      {suggestions.map((suggestion) => (
        <Suggestion
          key={suggestion.id}
          suggestion={suggestion}
          busy={working === suggestion.id}
          onAccept={onAccept}
          onReject={onReject}
        />
      ))}
    </ul>
  );
}

function Suggestion({
  suggestion,
  busy,
  onAccept,
  onReject,
}: {
  suggestion: MergeSuggestion;
  busy: boolean;
  onAccept: (id: string, keepEntityId: string) => void;
  onReject: (id: string) => void;
}) {
  return (
    <li className="flex flex-col gap-[var(--space-2)] rounded-[var(--radius-md)] border border-[var(--border-subtle)] bg-[var(--surface-1)] px-[var(--space-4)] py-[var(--space-3)]">
      <div className="flex items-start gap-[var(--space-2)]">
        <Merge className="mt-0.5 size-4 shrink-0 text-[var(--text-muted)]" aria-hidden />
        <p className="text-[length:var(--text-sm)] text-[var(--text-primary)]">
          &ldquo;{suggestion.entityAName}&rdquo; and &ldquo;{suggestion.entityBName}&rdquo; might be the same {suggestion.entityType}.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-[var(--space-2)] pl-[calc(var(--space-4)+var(--space-2))]">
        <button
          type="button"
          disabled={busy}
          onClick={() => onAccept(suggestion.id, suggestion.entityAId)}
          className="inline-flex items-center gap-1 rounded-[var(--radius-md)] border border-[var(--border-default)] px-[var(--space-3)] py-[var(--space-1)] text-[length:var(--text-xs)] font-medium text-[var(--text-primary)] hover:border-[var(--primary)] disabled:opacity-60"
        >
          <Check className="size-3" aria-hidden />
          Keep &ldquo;{suggestion.entityAName}&rdquo;
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => onAccept(suggestion.id, suggestion.entityBId)}
          className="inline-flex items-center gap-1 rounded-[var(--radius-md)] border border-[var(--border-default)] px-[var(--space-3)] py-[var(--space-1)] text-[length:var(--text-xs)] font-medium text-[var(--text-primary)] hover:border-[var(--primary)] disabled:opacity-60"
        >
          <Check className="size-3" aria-hidden />
          Keep &ldquo;{suggestion.entityBName}&rdquo;
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={() => onReject(suggestion.id)}
          className="inline-flex items-center gap-1 rounded-[var(--radius-md)] px-[var(--space-3)] py-[var(--space-1)] text-[length:var(--text-xs)] font-medium text-[var(--text-muted)] hover:text-[var(--text-primary)] disabled:opacity-60"
        >
          <X className="size-3" aria-hidden />
          Not the same
        </button>
      </div>
    </li>
  );
}

/**
 * A tab whose pass has not been built. Named honestly, with the increment that fills it —
 * the same answer every unfurnished destination gives, and a different
 * sentence from "there is nothing".
 */
function NotYet({ milestone }: { milestone: string }) {
  return (
    <div className="flex flex-col items-center gap-[var(--space-2)] rounded-[var(--radius-lg)] border border-dashed border-[var(--border-default)] px-[var(--space-6)] py-[var(--space-8)] text-center">
      <Undo2 className="size-5 text-[var(--text-muted)]" aria-hidden />
      <p className="text-[length:var(--text-sm)] text-[var(--text-primary)]">Not built yet.</p>
      <p className="text-[length:var(--text-xs)] text-[var(--text-muted)]">
        This tab arrives in {milestone}.
      </p>
    </div>
  );
}
